import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircleIcon, WarningIcon, EnvelopeIcon, WhatsappLogoIcon, PlugsConnectedIcon, MountainsIcon, ReceiptIcon, CalendarBlankIcon, CrownSimpleIcon, PlusIcon, TrashIcon, ArrowClockwiseIcon, TableIcon, PrinterIcon, FloppyDiskIcon, ClockCounterClockwiseIcon, BuildingsIcon, ForkKnifeIcon, MoonIcon } from "@phosphor-icons/react";
import TablesPanel from "@/pages/pos/TablesPanel";
import PropertyOverridesPanel from "@/pages/admin/PropertyOverridesPanel";
import DayEndLog from "@/pages/admin/DayEndLog";
import { SETTINGS } from "@/constants/testIds";
import { useAuth } from "@/lib/auth";

const StatusPill = ({ configured }) =>
  configured ? (
    <Badge variant="outline" className="bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]"><CheckCircleIcon size={12} className="mr-1" />Configured</Badge>
  ) : (
    <Badge variant="outline" className="bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"><WarningIcon size={12} className="mr-1" />Not set up</Badge>
  );

// ------------------- Platform / Group Panel -------------------
function PlatformPanel() {
  const [state, setState] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);
  const load = () => api.get("/settings/platform").then((r) => setState(r.data));
  useEffect(() => { load(); }, []);
  if (!state) return null;
  const save = async () => {
    const { id, updated_at, ...body } = state;
    await api.put("/settings/platform", body);
    toast.success("Group settings saved");
  };
  const set = (k, v) => setState({ ...state, [k]: v });
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/settings/platform/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      setState({ ...state, logo_url: r.data.logo_url });
      toast.success("Monogram uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const removeLogo = async () => {
    await api.delete("/settings/platform/logo");
    setState({ ...state, logo_url: null });
    toast.success("Monogram removed");
  };

  return (
    <Card className="p-8 max-w-3xl">
      <div className="flex items-center gap-3 mb-1">
        <MountainsIcon size={22} weight="duotone" className="text-primary" />
        <p className="label-overline">Hero</p>
      </div>
      <h3 className="font-display text-2xl">Hotel Group</h3>
      <p className="text-sm text-muted-foreground mt-1">This appears across the sign-in screen, sidebar, invoices, emails and WhatsApp messages.</p>

      {/* Logo uploader */}
      <div className="mt-6 flex items-center gap-4">
        <div className="h-20 w-20 rounded-full border-2 border-gold/50 bg-muted/40 flex items-center justify-center overflow-hidden">
          {state.logo_url ? (
            <img src={state.logo_url} alt="logo" className="max-h-16 max-w-16 object-contain" />
          ) : (
            <span className="text-gold text-3xl font-display">{(state.group_name || "?")[0]}</span>
          )}
        </div>
        <div>
          <p className="label-overline mb-1">Monogram / Wordmark</p>
          <input type="file" ref={fileInputRef} accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={onFile} className="hidden" data-testid="admin-logo-file" />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="admin-logo-upload">
              {uploading ? "Uploading…" : state.logo_url ? "Replace" : "Upload"}
            </Button>
            {state.logo_url && <Button size="sm" variant="ghost" onClick={removeLogo} data-testid="admin-logo-remove">Remove</Button>}
          </div>
          <p className="text-xs text-muted-foreground mt-1">PNG · JPEG · WebP · SVG, up to 1.5 MB</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2"><Label>Group name</Label><Input value={state.group_name || ""} onChange={(e) => set("group_name", e.target.value)} data-testid="admin-group-name" /></div>
        <div className="md:col-span-2"><Label>Tagline</Label><Input value={state.tagline || ""} onChange={(e) => set("tagline", e.target.value)} /></div>
        <div><Label>Contact email</Label><Input value={state.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} /></div>
        <div><Label>Contact phone</Label><Input value={state.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} /></div>
        <div className="md:col-span-2"><Label>Address</Label><Textarea rows={2} value={state.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
      </div>

      {/* KYC / ID card requirement — placed prominently under Group so front-desk & auditors see it immediately */}
      <div className="mt-6 rounded-md border-2 border-primary/30 bg-primary/5 p-4">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="kyc-toggle-platform"
            checked={!!state.kyc_required_for_checkin}
            onChange={(e) => set("kyc_required_for_checkin", e.target.checked)}
            className="mt-1 h-4 w-4 accent-primary"
            data-testid="admin-kyc-required-toggle"
          />
          <div className="flex-1">
            <label htmlFor="kyc-toggle-platform" className="font-medium cursor-pointer block">ID card mandatory for check-in</label>
            <p className="text-xs text-muted-foreground mt-1">
              When enabled, front-desk cannot check a guest in without both a photo <strong>and</strong> ID card image uploaded on the guest profile. Turn off to allow same-day walk-ins where ID is verified separately. Recommended: <strong>on</strong> for FRRO / GST compliance.
            </p>
            {!state.kyc_required_for_checkin && (
              <p className="text-xs text-[hsl(var(--warning))] mt-2 font-medium">⚠ Compliance bypass — check-ins will proceed without ID verification.</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-6">
        <Button onClick={save} data-testid="admin-group-save">Save</Button>
      </div>
    </Card>
  );
}

// ------------------- Communications Panel (guest emails) -------------------
function CommunicationsPanel() {
  const [state, setState] = useState(null);
  useEffect(() => { api.get("/settings/platform").then((r) => setState(r.data)); }, []);
  if (!state) return null;
  const set = (k, v) => setState({ ...state, [k]: v });
  const save = async () => {
    const { id, updated_at, ...body } = state;
    await api.put("/settings/platform", body);
    toast.success("Communication preferences saved");
  };
  return (
    <Card className="p-8 max-w-3xl">
      <div className="flex items-center gap-3 mb-1">
        <EnvelopeIcon size={22} weight="duotone" className="text-primary" />
        <p className="label-overline">Guest-facing</p>
      </div>
      <h3 className="font-display text-2xl">Booking confirmations</h3>
      <p className="text-sm text-muted-foreground mt-1">
        When enabled, a branded confirmation email is sent to the guest the moment a reservation is created (requires an Email integration).
      </p>

      <label className="flex items-center gap-3 mt-6 text-sm">
        <input type="checkbox" checked={!!state.send_confirmation_on_book} onChange={(e) => set("send_confirmation_on_book", e.target.checked)} data-testid="admin-conf-toggle" />
        Auto-send confirmation email on every new booking
      </label>

      <div className="mt-4">
        <Label>Email subject template</Label>
        <Input value={state.confirmation_subject || ""} onChange={(e) => set("confirmation_subject", e.target.value)} data-testid="admin-conf-subject" />
        <p className="text-xs text-muted-foreground mt-1">Use <span className="font-mono-plex">{"{property}"}</span> and <span className="font-mono-plex">{"{check_in}"}</span> as placeholders.</p>
      </div>

      <div className="flex justify-end mt-6"><Button onClick={save} data-testid="admin-conf-save">Save</Button></div>
    </Card>
  );
}

// ------------------- Invoice Settings Panel -------------------
function InvoicePanel() {
  const [state, setState] = useState(null);
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);
  useEffect(() => {
    api.get("/settings/platform").then((r) => setState(r.data));
    api.get("/reports/gst/history").then((r) => setHistory(r.data)).catch(() => {});
  }, []);
  if (!state) return null;
  const set = (k, v) => setState({ ...state, [k]: v });
  const save = async () => {
    const { id, updated_at, ...body } = state;
    await api.put("/settings/platform", body);
    toast.success("Invoice settings saved");
  };
  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/reports/gst/send-now");
      if (r.data?.ok) toast.success(`GST report sent to ${r.data.sent_to}`);
      else toast.error(r.data?.error?.slice(0, 200) || r.data?.reason || "Send failed");
      const h = await api.get("/reports/gst/history"); setHistory(h.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setRunning(false); }
  };
  return (
    <div className="space-y-6 max-w-3xl">
      <Card className="p-8">
        <div className="flex items-center gap-3 mb-1">
          <ReceiptIcon size={22} weight="duotone" className="text-primary" />
          <p className="label-overline">Billing defaults</p>
        </div>
        <h3 className="font-display text-2xl">Invoice</h3>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Invoice prefix</Label><Input value={state.invoice_prefix || ""} onChange={(e) => set("invoice_prefix", e.target.value)} placeholder="INV" /></div>
          <div><Label>Default GST rate (%)</Label><Input type="number" step="0.5" value={state.default_tax_rate || 12} onChange={(e) => set("default_tax_rate", Number(e.target.value))} /></div>
          <div className="md:col-span-2">
            <Label>Default GST treatment for new invoices</Label>
            <div className="mt-2 grid grid-cols-2 gap-2" data-testid="admin-default-tax-mode">
              <button
                type="button"
                onClick={() => set("default_tax_inclusive", false)}
                className={`text-left rounded-md border p-3 text-sm transition ${!state.default_tax_inclusive ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
                data-testid="admin-tax-default-exclusive"
              >
                <div className="font-medium">Exclusive of GST</div>
                <div className="text-xs text-muted-foreground mt-0.5">GST added on top when generating an invoice.</div>
              </button>
              <button
                type="button"
                onClick={() => set("default_tax_inclusive", true)}
                className={`text-left rounded-md border p-3 text-sm transition ${state.default_tax_inclusive ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
                data-testid="admin-tax-default-inclusive"
              >
                <div className="font-medium">Inclusive of GST</div>
                <div className="text-xs text-muted-foreground mt-0.5">Room rates already include GST; back-calculated.</div>
              </button>
            </div>
          </div>
          <div><Label>GST number</Label><Input value={state.gst_number || ""} onChange={(e) => set("gst_number", e.target.value)} /></div>
          <div><Label>Currency symbol</Label><Input value={state.currency_symbol || "₹"} onChange={(e) => set("currency_symbol", e.target.value)} /></div>

          <div className="md:col-span-2 rounded-md border border-border p-4">
            <p className="label-overline mb-2">Restaurant branding (optional)</p>
            <p className="text-xs text-muted-foreground mb-3">Override the group name / GST# on POS receipts and KOT if your restaurant runs under a separate brand or GSTIN.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Restaurant name</Label>
                <Input value={state.restaurant_name || ""} onChange={(e) => set("restaurant_name", e.target.value)} placeholder="Leave blank to reuse group name" data-testid="admin-restaurant-name" />
              </div>
              <div>
                <Label>Restaurant GST #</Label>
                <Input value={state.restaurant_gst_number || ""} onChange={(e) => set("restaurant_gst_number", e.target.value)} placeholder="Leave blank to reuse hotel GSTIN" data-testid="admin-restaurant-gst" />
              </div>
            </div>
          </div>

          <div className="md:col-span-2">
            <Label>Expense manager-approval threshold (₹)</Label>
            <Input
              type="number"
              step="100"
              value={state.expense_approval_threshold ?? 10000}
              onChange={(e) => set("expense_approval_threshold", Number(e.target.value))}
              data-testid="admin-expense-threshold"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Any expense at or above this amount, filed by a non-admin, will land in pending approval.
              Set to <code>0</code> to disable and auto-approve everything.
            </p>
          </div>
          <div className="md:col-span-2"><Label>Invoice footer</Label><Input value={state.invoice_footer || ""} onChange={(e) => set("invoice_footer", e.target.value)} /></div>
          <div className="md:col-span-2"><Label>Terms & conditions</Label><Textarea rows={3} value={state.invoice_terms || ""} onChange={(e) => set("invoice_terms", e.target.value)} /></div>

          <div className="md:col-span-2 rounded-md border border-border p-4" data-testid="admin-invoice-layout">
            <p className="label-overline mb-1">Invoice layout</p>
            <p className="text-xs text-muted-foreground mb-3">Choose which sections print on every invoice (both PDF and browser preview).</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              {[
                ["show_property_gstin", "Property GSTIN"],
                ["show_property_address", "Property address"],
                ["show_property_contact", "Property phone & email"],
                ["show_logo", "Property logo"],
                ["show_booking_ref", "Booking reference"],
                ["show_stay_details", "Stay details (room, dates, guests)"],
                ["show_guest_id", "Guest ID (KYC)"],
                ["show_guest_gstin_pan", "Guest GSTIN / PAN"],
                ["show_corporate_block", "Corporate bill-to block"],
                ["show_tax_breakdown", "Per-section GST breakdown"],
                ["show_deposit_line", "Deposit applied line"],
                ["show_balance_due", "Balance due line"],
                ["show_payment_reference", "Payment reference / UTR"],
                ["show_notes", "Notes section"],
                ["show_footer_signature", 'Footer text ("Thank you…")'],
                ["show_computer_generated_line", '"Computer-generated" line'],
              ].map(([key, label]) => {
                const val = (state.invoice_layout || {})[key];
                const checked = val === undefined ? true : !!val;
                return (
                  <label key={key} className="flex items-center gap-2 cursor-pointer rounded-md border border-border/60 px-2 py-1.5" data-testid={`invoice-layout-${key}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => set("invoice_layout", { ...(state.invoice_layout || {}), [key]: e.target.checked })}
                      className="accent-primary"
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
            <div className="mt-3">
              <Label className="text-xs">Additional footer note (optional)</Label>
              <Textarea
                rows={2}
                value={(state.invoice_layout || {}).custom_footer_html || ""}
                onChange={(e) => set("invoice_layout", { ...(state.invoice_layout || {}), custom_footer_html: e.target.value })}
                placeholder="e.g. HSN 996311 · Bank details for NEFT · SAC 9963 …"
                data-testid="invoice-layout-custom-footer"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-6"><Button onClick={save} data-testid="admin-invoice-save">Save</Button></div>
      </Card>

      <Card className="p-8">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-3">
            <CalendarBlankIcon size={22} weight="duotone" className="text-primary" />
            <p className="label-overline">Automation</p>
          </div>
          <Badge variant="outline" className={state.ca_report_enabled ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" : "bg-muted"}>
            {state.ca_report_enabled ? "Auto-send ON" : "Auto-send OFF"}
          </Badge>
        </div>
        <h3 className="font-display text-2xl">Monthly GST Report → CA</h3>
        <p className="text-sm text-muted-foreground mt-1">
          On the 1st of every month at 09:00 IST, we email the previous month's GST report (PDF attachment) to your CA. Email must be configured on the Email tab.
        </p>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Label>CA email address</Label><Input type="email" value={state.ca_email || ""} onChange={(e) => set("ca_email", e.target.value)} placeholder="ca@yourfirm.com" data-testid="admin-ca-email" /></div>
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <input type="checkbox" checked={!!state.ca_report_enabled} onChange={(e) => set("ca_report_enabled", e.target.checked)} data-testid="admin-ca-enabled" />
            Auto-send the monthly report on the 1st
          </label>
        </div>
        <div className="flex flex-wrap gap-2 justify-end mt-6">
          <Button variant="outline" onClick={runNow} disabled={running} data-testid="admin-ca-send-now">
            {running ? "Sending…" : "Send previous month now"}
          </Button>
          <Button onClick={save}>Save</Button>
        </div>

        {history.length > 0 && (
          <div className="mt-6 pt-6 border-t border-border">
            <p className="label-overline mb-2">Recent runs</p>
            <ul className="text-sm space-y-1">
              {history.slice(0, 5).map((h) => (
                <li key={h.id} className="flex justify-between">
                  <span>{h.period_start} → {h.period_end} · <span className="text-muted-foreground">{h.trigger}</span></span>
                  <span className="text-muted-foreground text-xs">{h.sent_to} · {h.sent_at?.slice(0, 16).replace("T", " ")}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>
    </div>
  );
}

// ------------------- Email Panel -------------------
function EmailPanel() {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({ api_key: "", from_email: "", from_name: "" });
  const load = () => api.get("/settings/email").then((r) => setState(r.data));
  useEffect(() => { load(); }, []);
  if (!state) return null;
  const save = async (e) => {
    e.preventDefault();
    const body = {};
    if (form.api_key) body.api_key = form.api_key;
    if (form.from_email) body.from_email = form.from_email;
    if (form.from_name !== "") body.from_name = form.from_name;
    await api.put("/settings/email", body);
    toast.success("Email settings saved");
    setForm({ api_key: "", from_email: "", from_name: "" });
    load();
  };
  return (
    <Card className="p-8 max-w-2xl">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          <EnvelopeIcon size={22} weight="duotone" className="text-primary" />
          <p className="label-overline">Integration</p>
        </div>
        <StatusPill configured={state.configured} />
      </div>
      <h3 className="font-display text-2xl">Email · Resend</h3>
      <p className="text-sm text-muted-foreground mt-1">
        Used to email invoices and booking confirmations. Get an API key at <a className="underline" href="https://resend.com/api-keys" target="_blank" rel="noreferrer">resend.com/api-keys</a>.
      </p>

      {state.configured && (
        <div className="mt-4 rounded-md bg-muted/60 p-3 text-xs space-y-1">
          <div><span className="text-muted-foreground">From:</span> {state.from_name || ""} &lt;{state.from_email}&gt;</div>
          <div><span className="text-muted-foreground">Key:</span> <span className="font-mono-plex">{state.api_key_masked}</span></div>
        </div>
      )}

      <form onSubmit={save} className="mt-6 space-y-4">
        <div><Label>API key</Label><Input type="password" placeholder="re_..." value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} data-testid="admin-email-key" /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>From email</Label><Input type="email" placeholder="invoices@yourhotel.com" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} data-testid="admin-email-from" /></div>
          <div><Label>From name</Label><Input placeholder="Kanchenjunga Hotels" value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} /></div>
        </div>
        <div className="flex justify-end"><Button type="submit" data-testid="admin-email-save">Save</Button></div>
      </form>
    </Card>
  );
}

// ------------------- WhatsApp Panel -------------------
function WhatsAppPanel() {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({ account_sid: "", auth_token: "", from_number: "" });
  const load = () => api.get("/settings/whatsapp").then((r) => setState(r.data));
  useEffect(() => { load(); }, []);
  if (!state) return null;
  const save = async (e) => {
    e.preventDefault();
    const body = {};
    for (const k of ["account_sid", "auth_token", "from_number"]) if (form[k]) body[k] = form[k];
    await api.put("/settings/whatsapp", body);
    toast.success("WhatsApp settings saved");
    setForm({ account_sid: "", auth_token: "", from_number: "" });
    load();
  };
  return (
    <Card className="p-8 max-w-2xl">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          <WhatsappLogoIcon size={22} weight="duotone" className="text-primary" />
          <p className="label-overline">Integration</p>
        </div>
        <StatusPill configured={state.configured} />
      </div>
      <h3 className="font-display text-2xl">WhatsApp · Twilio</h3>
      <p className="text-sm text-muted-foreground mt-1">
        Sends booking confirmations and invoice links via WhatsApp. Use the Twilio Sandbox <span className="font-mono-plex">whatsapp:+14155238886</span> for testing.
      </p>

      {state.configured && (
        <div className="mt-4 rounded-md bg-muted/60 p-3 text-xs space-y-1">
          <div><span className="text-muted-foreground">SID:</span> <span className="font-mono-plex">{state.account_sid_masked}</span></div>
          <div><span className="text-muted-foreground">Token:</span> <span className="font-mono-plex">{state.auth_token_masked}</span></div>
          <div><span className="text-muted-foreground">From:</span> {state.from_number}</div>
        </div>
      )}

      <form onSubmit={save} className="mt-6 space-y-4">
        <div><Label>Account SID</Label><Input placeholder="AC..." value={form.account_sid} onChange={(e) => setForm({ ...form, account_sid: e.target.value })} data-testid="admin-wa-sid" /></div>
        <div><Label>Auth token</Label><Input type="password" value={form.auth_token} onChange={(e) => setForm({ ...form, auth_token: e.target.value })} data-testid="admin-wa-token" /></div>
        <div><Label>From number</Label><Input placeholder="whatsapp:+14155238886" value={form.from_number} onChange={(e) => setForm({ ...form, from_number: e.target.value })} data-testid="admin-wa-from" /></div>
        <div className="flex justify-end"><Button type="submit" data-testid="admin-wa-save">Save</Button></div>
      </form>
    </Card>
  );
}

// ------------------- OTA Credentials Panel -------------------
const OTA_LIST = [
  { id: "booking_com", name: "Booking.com Connectivity", color: "#003580", fields: ["hotel_id", "username", "password", "endpoint_url"] },
  { id: "makemytrip", name: "MakeMyTrip Connect", color: "#EB2226", fields: ["hotel_id", "partner_id", "api_key", "endpoint_url"] },
  { id: "expedia", name: "Expedia EPS", color: "#FFC500", fields: ["hotel_id", "api_key", "endpoint_url"] },
  { id: "airbnb", name: "Airbnb Host API", color: "#FF5A5F", fields: ["hotel_id", "api_key"] },
  { id: "agoda", name: "Agoda YCS", color: "#5D3E9C", fields: ["hotel_id", "username", "password"] },
  { id: "goibibo", name: "Goibibo", color: "#F58220", fields: ["hotel_id", "api_key"] },
  { id: "yatra", name: "Yatra Partner Connect", color: "#F58220", fields: ["hotel_id", "api_key", "endpoint_url"] },
];

function OtaCard({ ota, propertyId }) {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({});
  const load = () => api.get(`/settings/ota/${ota.id}`, { params: propertyId ? { property_id: propertyId } : {} }).then((r) => setState(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ota.id, propertyId]);
  const save = async (e) => {
    e.preventDefault();
    const body = { property_id: propertyId || undefined };
    Object.entries(form).forEach(([k, v]) => { if (v) body[k] = v; });
    await api.put(`/settings/ota/${ota.id}`, body);
    toast.success(`${ota.name} credentials saved${propertyId ? " for this property" : ""}`);
    setForm({});
    load();
  };
  const label = { hotel_id: "Hotel ID", username: "Username", password: "Password", api_key: "API key", partner_id: "Partner ID", endpoint_url: "Endpoint URL" };
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md flex items-center justify-center text-white font-bold" style={{ background: ota.color }}>{ota.name[0]}</div>
          <div>
            <h4 className="font-display text-base">{ota.name}</h4>
            {state?.hotel_id && <p className="text-xs text-muted-foreground">Hotel: <span className="font-mono-plex">{state.hotel_id}</span></p>}
            {state?.inherited_from_group && <p className="text-[10px] text-muted-foreground italic">Inherited from group defaults · save to override for this property</p>}
          </div>
        </div>
        {state && <StatusPill configured={state.configured} />}
      </div>
      <form onSubmit={save} className="space-y-3">
        {ota.fields.map((f) => (
          <div key={f}>
            <Label className="text-xs">{label[f]}</Label>
            <Input
              type={f === "password" || f === "api_key" ? "password" : "text"}
              placeholder={state?.[f + "_masked"] || state?.[f] || ""}
              value={form[f] || ""}
              onChange={(e) => setForm({ ...form, [f]: e.target.value })}
              data-testid={`admin-ota-${ota.id}-${f}`}
            />
          </div>
        ))}
        <div className="flex justify-end pt-2"><Button size="sm" type="submit" data-testid={`admin-ota-${ota.id}-save`}>Save</Button></div>
      </form>
    </Card>
  );
}

function OtaPanel() {
  const [properties, setProperties] = useState([]);
  const [pid, setPid] = useState("");
  useEffect(() => { api.get("/properties").then((r) => { setProperties(r.data); if (r.data[0]) setPid(r.data[0].id); }); }, []);
  return (
    <div>
      <Card className="p-4 mb-4 border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 flex gap-3 items-start">
        <WarningIcon size={20} className="text-[hsl(var(--warning))] mt-0.5" />
        <div className="text-sm">
          <p className="font-medium">Credential-only setup · scoped to the selected property</p>
          <p className="text-muted-foreground">Each hotel has its own OTA credentials. Actual inventory push remains stubbed until you're onboarded as a connectivity partner with each OTA.</p>
        </div>
      </Card>
      <Card className="p-3 mb-4 flex items-center gap-3">
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Property</Label>
        <Select value={pid} onValueChange={setPid}>
          <SelectTrigger className="w-72" data-testid="admin-ota-property-picker"><SelectValue placeholder="Select property" /></SelectTrigger>
          <SelectContent>
            {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.city}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {OTA_LIST.map((o) => <OtaCard key={o.id} ota={o} propertyId={pid} />)}
      </div>
    </div>
  );
}

export default function AdminConsole() {
  const { user } = useAuth();
  const [master, setMaster] = useState(null);
  useEffect(() => { api.get("/platform/master").then((r) => setMaster(r.data)); }, []);
  const isMaster = !!master?.is_master;

  const forceUpdate = async () => {
    if (!window.confirm("Reload the app to the latest version? Any unsaved work in tabs will be lost.")) return;
    try {
      // Clear service worker caches
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      // Tell active service worker(s) to skip waiting and take over
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const reg of regs) {
          if (reg.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
          try { await reg.update(); } catch (_) {}
        }
      }
      toast.success("Fetching newest build…");
      setTimeout(() => window.location.reload(true), 400);
    } catch (e) {
      toast.error("Update failed — try refreshing manually");
    }
  };

  return (
    <div>
      <PageHeader
        overline="Super admin"
        title="Admin Console"
        description="One place to manage branding, invoices, integrations and OTA credentials."
      />

      <Card className="p-4 mb-6 flex flex-wrap items-center gap-3 border-primary/30 bg-primary/5" data-testid="admin-app-update-card">
        <ArrowClockwiseIcon size={22} weight="duotone" className="text-primary" />
        <div className="flex-1 min-w-[200px]">
          <p className="label-overline">App version</p>
          <p className="text-sm">Force-load the latest PMS build on this device. Clears cached scripts and re-registers the service worker.</p>
        </div>
        <Button onClick={forceUpdate} data-testid="admin-app-update-btn">
          <ArrowClockwiseIcon size={14} className="mr-1" /> Update app
        </Button>
      </Card>

      <Tabs defaultValue="platform" orientation="vertical" className="flex flex-col lg:flex-row gap-6" data-testid="admin-tabs-root">
        <TabsList className="flex flex-row lg:flex-col h-auto items-stretch justify-start lg:w-56 lg:sticky lg:top-4 self-start p-1.5 gap-1 overflow-x-auto lg:overflow-visible">
          <TabsTrigger value="platform" className="justify-start w-full" data-testid="admin-tab-platform"><MountainsIcon size={14} className="mr-2" />Group</TabsTrigger>
          <TabsTrigger value="invoice" className="justify-start w-full" data-testid="admin-tab-invoice"><ReceiptIcon size={14} className="mr-2" />Invoice</TabsTrigger>
          <TabsTrigger value="comms" className="justify-start w-full" data-testid="admin-tab-comms"><EnvelopeIcon size={14} className="mr-2" />Guest emails</TabsTrigger>
          <TabsTrigger value="email" className="justify-start w-full" data-testid="admin-tab-email"><EnvelopeIcon size={14} className="mr-2" />Email</TabsTrigger>
          <TabsTrigger value="whatsapp" className="justify-start w-full" data-testid="admin-tab-whatsapp"><WhatsappLogoIcon size={14} className="mr-2" />WhatsApp</TabsTrigger>
          <TabsTrigger value="gst" className="justify-start w-full" data-testid="admin-tab-gst"><ReceiptIcon size={14} className="mr-2" />GST</TabsTrigger>
          <TabsTrigger value="meal-plans" className="justify-start w-full" data-testid="admin-tab-meal-plans"><ForkKnifeIcon size={14} className="mr-2" />Meal Plans</TabsTrigger>
          <TabsTrigger value="menu-order" className="justify-start w-full" data-testid="admin-tab-menu-order"><ReceiptIcon size={14} className="mr-2" />Sidebar Order</TabsTrigger>
          <TabsTrigger value="approval" className="justify-start w-full" data-testid="admin-tab-approval"><CheckCircleIcon size={14} className="mr-2" />Approval</TabsTrigger>
          <TabsTrigger value="tables" className="justify-start w-full" data-testid="admin-tab-tables"><TableIcon size={14} className="mr-2" />Tables</TabsTrigger>
          <TabsTrigger value="printing" className="justify-start w-full" data-testid="admin-tab-printing"><PrinterIcon size={14} className="mr-2" />Printing</TabsTrigger>
          <TabsTrigger value="day-end-log" className="justify-start w-full" data-testid="admin-tab-day-end-log"><MoonIcon size={14} className="mr-2" />Day End Log</TabsTrigger>
          {isMaster && <TabsTrigger value="backup" className="justify-start w-full" data-testid="admin-tab-backup"><FloppyDiskIcon size={14} className="mr-2" />Backups</TabsTrigger>}
          <TabsTrigger value="ota" className="justify-start w-full" data-testid="admin-tab-ota"><PlugsConnectedIcon size={14} className="mr-2" />OTA</TabsTrigger>
          {isMaster && <TabsTrigger value="master" className="justify-start w-full" data-testid="admin-tab-master"><CrownSimpleIcon size={14} className="mr-2" />Master</TabsTrigger>}
        </TabsList>

        <div className="flex-1 min-w-0">
          <TabsContent value="platform" className="mt-0"><PlatformPanel /></TabsContent>
          <TabsContent value="invoice" className="mt-0"><PropertyOverridesPanel sections={["invoice"]} /></TabsContent>
          <TabsContent value="comms" className="mt-0"><CommunicationsPanel /></TabsContent>
          <TabsContent value="email" className="mt-0"><EmailPanel /></TabsContent>
          <TabsContent value="whatsapp" className="mt-0"><WhatsAppPanel /></TabsContent>
          <TabsContent value="gst" className="mt-0"><PropertyOverridesPanel sections={["gst"]} /></TabsContent>
          <TabsContent value="meal-plans" className="mt-0"><PropertyOverridesPanel sections={["meal_plans"]} /></TabsContent>
          <TabsContent value="menu-order" className="mt-0"><MenuOrderPanel /></TabsContent>
          <TabsContent value="approval" className="mt-0"><ApprovalPanel /></TabsContent>
          <TabsContent value="tables" className="mt-0"><TablesPanel /></TabsContent>
          <TabsContent value="printing" className="mt-0"><PrintingPanel /></TabsContent>
          <TabsContent value="day-end-log" className="mt-0"><DayEndLog /></TabsContent>
          {isMaster && <TabsContent value="backup" className="mt-0"><BackupPanel /></TabsContent>}
          <TabsContent value="ota" className="mt-0"><OtaPanel /></TabsContent>
          {isMaster && (
            <TabsContent value="master" className="mt-0">
              <MasterPanel master={master} onChanged={() => api.get("/platform/master").then((r) => setMaster(r.data))} />
            </TabsContent>
          )}
        </div>
      </Tabs>
    </div>
  );
}

// ------------------- GST Panel -------------------
function GstPanel() {
  const [state, setState] = useState(null);
  const load = () => api.get("/settings/platform").then((r) => setState(r.data));
  useEffect(() => { load(); }, []);
  if (!state) return null;
  const set = (k, v) => setState({ ...state, [k]: v });
  const save = async () => {
    const { id, updated_at, ...body } = state;
    await api.put("/settings/platform", body);
    toast.success("GST configuration saved");
    load();
  };
  const updateSlab = (idx, patch) => {
    const slabs = [...(state.gst_slabs || [])];
    slabs[idx] = { ...slabs[idx], ...patch };
    set("gst_slabs", slabs);
  };
  const addSlab = () => set("gst_slabs", [...(state.gst_slabs || []), {
    id: `slab-${Date.now()}`, min_rate: 0, max_rate: null, gst_rate: 12, label: "New slab",
  }]);
  const removeSlab = (idx) => set("gst_slabs", (state.gst_slabs || []).filter((_, i) => i !== idx));

  const updatePosSlab = (idx, patch) => {
    const slabs = [...(state.pos_gst_slabs || [])];
    slabs[idx] = { ...slabs[idx], ...patch };
    set("pos_gst_slabs", slabs);
  };
  const addPosSlab = () => set("pos_gst_slabs", [...(state.pos_gst_slabs || []), {
    id: `pos-slab-${Date.now()}`, category: "", gst_rate: 5, label: "New POS category",
  }]);
  const removePosSlab = (idx) => set("pos_gst_slabs", (state.pos_gst_slabs || []).filter((_, i) => i !== idx));
  return (
    <Card className="p-8 max-w-4xl">
      <div className="flex items-center gap-3 mb-1">
        <ReceiptIcon size={22} weight="duotone" className="text-primary" />
        <p className="label-overline">Tax configuration</p>
      </div>
      <h3 className="font-display text-2xl">GST · rates, slabs & identity</h3>
      <p className="text-sm text-muted-foreground mt-1">
        Configure GSTIN, default rates and Indian slab-wise room-tariff rules. Applied automatically when generating invoices.
      </p>

      {/* Identity */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
        <div>
          <Label className="text-xs">GSTIN</Label>
          <Input value={state.gst_number || ""} onChange={(e) => set("gst_number", e.target.value)} data-testid="gst-gstin" />
        </div>
        <div>
          <Label className="text-xs">PAN</Label>
          <Input value={state.pan_number || ""} onChange={(e) => set("pan_number", e.target.value)} data-testid="gst-pan" />
        </div>
        <div>
          <Label className="text-xs">State code (2-digit)</Label>
          <Input value={state.state_code || ""} onChange={(e) => set("state_code", e.target.value)} placeholder="10 = Sikkim" data-testid="gst-state" />
        </div>
      </div>

      {/* Rate defaults */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
        <div>
          <Label className="text-xs">Default room GST (%)</Label>
          <Input type="number" step="0.5" value={state.default_tax_rate ?? 12} onChange={(e) => set("default_tax_rate", Number(e.target.value))} data-testid="gst-default-room-rate" />
        </div>
        <div>
          <Label className="text-xs">Default POS GST (%)</Label>
          <Input type="number" step="0.5" value={state.pos_default_gst_rate ?? 5} onChange={(e) => set("pos_default_gst_rate", Number(e.target.value))} data-testid="gst-default-pos-rate" />
        </div>
        <div>
          <Label className="text-xs">Default treatment</Label>
          <select
            value={state.default_tax_inclusive ? "inclusive" : "exclusive"}
            onChange={(e) => set("default_tax_inclusive", e.target.value === "inclusive")}
            className="h-10 mt-1 rounded-md border border-input bg-background px-3 text-sm w-full"
            data-testid="gst-default-treatment"
          >
            <option value="exclusive">Exclusive (add GST on top)</option>
            <option value="inclusive">Inclusive (back-calculate)</option>
          </select>
        </div>
      </div>

      {/* Slabs */}
      <div className="mt-8 rounded-md border border-border">
        <div className="flex items-center justify-between p-3 border-b border-border">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={!!state.gst_slabs_enabled}
              onChange={(e) => set("gst_slabs_enabled", e.target.checked)}
              data-testid="gst-slabs-toggle"
            />
            <span>Use slab-based room GST by nightly tariff</span>
          </label>
          <Button size="sm" variant="outline" onClick={addSlab} data-testid="gst-add-slab"><PlusIcon size={12} className="mr-1" /> Add slab</Button>
        </div>
        <div className="p-3 space-y-2">
          <p className="text-[10px] text-muted-foreground">India norm: ≤ ₹7,500 = 12% · &gt; ₹7,500 = 18%. Slabs match nightly rate ranges — invoice generator picks the row where <code>min ≤ nightly ≤ max</code>.</p>
          {(state.gst_slabs || []).map((slab, idx) => (
            <div key={slab.id || idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`gst-slab-row-${idx}`}>
              <div className="col-span-4"><Input value={slab.label || ""} onChange={(e) => updateSlab(idx, { label: e.target.value })} placeholder="Label" /></div>
              <div className="col-span-2"><Input type="number" step="0.01" value={slab.min_rate ?? 0} onChange={(e) => updateSlab(idx, { min_rate: Number(e.target.value) })} placeholder="min ₹" /></div>
              <div className="col-span-2">
                <Input
                  type="number"
                  step="0.01"
                  value={slab.max_rate == null ? "" : slab.max_rate}
                  onChange={(e) => updateSlab(idx, { max_rate: e.target.value === "" ? null : Number(e.target.value) })}
                  placeholder="max ₹ (∞ if empty)"
                />
              </div>
              <div className="col-span-2"><Input type="number" step="0.5" value={slab.gst_rate ?? 12} onChange={(e) => updateSlab(idx, { gst_rate: Number(e.target.value) })} placeholder="GST %" /></div>
              <div className="col-span-2 flex justify-end">
                <Button size="icon" variant="ghost" onClick={() => removeSlab(idx)} className="text-destructive" data-testid={`gst-remove-slab-${idx}`}><TrashIcon size={12} /></Button>
              </div>
            </div>
          ))}
          {(state.gst_slabs || []).length === 0 && (
            <p className="text-xs text-muted-foreground italic p-2">No slabs defined. Falls back to default room GST.</p>
          )}
        </div>
      </div>

      {/* POS slabs by category */}
      <div className="mt-6 rounded-md border border-border">
        <div className="flex items-center justify-between p-3 border-b border-border">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={!!state.pos_gst_slabs_enabled}
              onChange={(e) => set("pos_gst_slabs_enabled", e.target.checked)}
              data-testid="pos-gst-slabs-toggle"
            />
            <span>Use slab-based POS GST by menu category</span>
          </label>
          <Button size="sm" variant="outline" onClick={addPosSlab} data-testid="pos-gst-add-slab"><PlusIcon size={12} className="mr-1" /> Add row</Button>
        </div>
        <div className="p-3 space-y-2">
          <p className="text-[10px] text-muted-foreground">Restaurant POS lines are re-taxed using these rates when the toggle is on — matches the category on the menu item (Food = 5%, Bar = 18%, etc.).</p>
          {(state.pos_gst_slabs || []).map((slab, idx) => (
            <div key={slab.id || idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`pos-gst-slab-row-${idx}`}>
              <div className="col-span-5"><Input value={slab.label || ""} onChange={(e) => updatePosSlab(idx, { label: e.target.value })} placeholder="Description" /></div>
              <div className="col-span-3"><Input value={slab.category || ""} onChange={(e) => updatePosSlab(idx, { category: e.target.value })} placeholder="Menu category (e.g. Food)" /></div>
              <div className="col-span-2"><Input type="number" step="0.5" value={slab.gst_rate ?? 5} onChange={(e) => updatePosSlab(idx, { gst_rate: Number(e.target.value) })} placeholder="GST %" /></div>
              <div className="col-span-2 flex justify-end">
                <Button size="icon" variant="ghost" onClick={() => removePosSlab(idx)} className="text-destructive" data-testid={`pos-gst-remove-slab-${idx}`}><TrashIcon size={12} /></Button>
              </div>
            </div>
          ))}
          {(state.pos_gst_slabs || []).length === 0 && (
            <p className="text-xs text-muted-foreground italic p-2">No POS category slabs — per-item GST from the menu is used.</p>
          )}
        </div>
      </div>

      <div className="flex justify-end mt-6"><Button onClick={save} data-testid="gst-save-btn">Save GST configuration</Button></div>
    </Card>
  );
}

// ------------------- Backup / Restore Panel -------------------
function BackupPanel() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dir, setDir] = useState("");
  const [master, setMaster] = useState(null);
  const load = () => api.get("/admin/backup/list").then((r) => { setRows(r.data.backups || []); setDir(r.data.backup_dir || ""); }).catch(() => {});
  useEffect(() => {
    load();
    api.get("/platform/master").then((r) => setMaster(r.data)).catch(() => {});
  }, []);
  const isMaster = !!master?.is_master;

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/backup/now");
      toast.success(`Snapshot written · ${r.data.backups?.length || 0} kept`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Backup failed"); }
    finally { setBusy(false); }
  };

  const restore = async (filename) => {
    if (!window.confirm(`Restore Mongo from ${filename}?\n\nThis will REPLACE every collection with the snapshot's contents. A safety snapshot of the current state will be auto-created first.\n\nProceed?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/backup/restore", { filename, confirm: true });
      toast.success(`Restored ${r.data.docs_restored} documents from ${filename}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5 space-y-4" data-testid="admin-backup-panel">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-display text-2xl">Automatic backups</h3>
          <p className="text-sm text-muted-foreground">Full Mongo snapshot at 02:00 IST daily · retention 7 days · manual snapshot anytime · one-click restore.</p>
          {dir && <p className="text-[10px] text-muted-foreground mt-1 font-mono-plex">Stored in {dir}</p>}
        </div>
        <Button onClick={runNow} disabled={busy} data-testid="backup-run-now"><ArrowClockwiseIcon size={14} className="mr-1" /> Snapshot now</Button>
      </div>

      <div className="rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-3 text-xs">
        <WarningIcon size={12} className="inline mr-1 text-[hsl(var(--warning))]" />
        <strong>Restore is destructive.</strong> Every collection in the snapshot is dropped and reinserted. A pre-restore snapshot is auto-created before the operation so you can always rewind. {!isMaster && <span className="font-medium">Only the owner ({master?.master_email || "master"}) can trigger a restore.</span>}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-left">
              <th className="p-2">Snapshot</th>
              <th className="p-2">Created</th>
              <th className="p-2 text-right">Size</th>
              <th className="p-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={4} className="text-center py-6 text-muted-foreground italic">No backups yet — hit "Snapshot now" to create one.</td></tr>
            )}
            {rows.map((b, i) => (
              <tr key={b.name} className="border-t border-border" data-testid={`backup-row-${i}`}>
                <td className="p-2 font-mono-plex text-xs">{b.name}</td>
                <td className="p-2 text-xs">{new Date(b.created).toLocaleString("en-IN")}</td>
                <td className="p-2 text-right text-xs font-mono-plex">{b.size_kb} KB</td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="outline" disabled={busy || !isMaster} onClick={() => restore(b.name)} data-testid={`backup-restore-${i}`} title={isMaster ? "Restore Mongo from this snapshot" : "Only the owner can restore"}>
                    <ClockCounterClockwiseIcon size={12} className="mr-1" /> Restore
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}


// ------------------- Printing Panel -------------------
function PrintingPanel() {
  const [state, setState] = useState(null);
  const load = () => api.get("/settings/platform").then((r) => setState(r.data));
  useEffect(() => { load(); }, []);
  if (!state) return null;
  const set = (k, v) => setState({ ...state, [k]: v });
  const save = async () => {
    await api.put("/settings/platform", {
      printer_paper_width_mm: Number(state.printer_paper_width_mm) || 80,
      printer_header_text: state.printer_header_text || "",
      printer_footer_text: state.printer_footer_text || "",
      printer_auto_print_kot: !!state.printer_auto_print_kot,
      printer_auto_print_bill: !!state.printer_auto_print_bill,
      escpos_printer_ip: state.escpos_printer_ip || "",
      escpos_printer_port: Number(state.escpos_printer_port) || 9100,
      escpos_kot_ip: state.escpos_kot_ip || "",
      escpos_kot_port: Number(state.escpos_kot_port) || 9100,
    });
    toast.success("Printing settings saved");
  };
  return (
    <Card className="p-5 space-y-4" data-testid="admin-printing-panel">
      <div>
        <h3 className="font-display text-2xl">Thermal receipt & KOT printing</h3>
        <p className="text-sm text-muted-foreground">Configure paper roll size, header/footer, and (optionally) a networked ESC/POS printer for direct printing from the backend.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Paper width</Label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {[58, 80].map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => set("printer_paper_width_mm", w)}
                className={`px-4 py-3 rounded-md border-2 text-center transition ${Number(state.printer_paper_width_mm) === w ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}
                data-testid={`printing-width-${w}`}
              >
                <p className="font-display text-xl">{w}mm</p>
                <p className="text-[10px] text-muted-foreground">{w === 58 ? "Compact receipt" : "Standard receipt"}</p>
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!state.printer_auto_print_kot} onChange={(e) => set("printer_auto_print_kot", e.target.checked)} data-testid="printing-auto-kot" />
            <span>Auto-open KOT print window when staff sends a round to the kitchen</span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!state.printer_auto_print_bill} onChange={(e) => set("printer_auto_print_bill", e.target.checked)} data-testid="printing-auto-bill" />
            <span>Auto-open the customer receipt after closing a table</span>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Header text (extra line above hotel name)</Label>
          <Input value={state.printer_header_text || ""} onChange={(e) => set("printer_header_text", e.target.value)} placeholder="e.g. FSSAI 12345 · +91-9876543210" data-testid="printing-header-text" />
        </div>
        <div>
          <Label>Footer text (thank-you line)</Label>
          <Input value={state.printer_footer_text || ""} onChange={(e) => set("printer_footer_text", e.target.value)} placeholder="Thank you. Please visit again." data-testid="printing-footer-text" />
        </div>
      </div>

      <div className="rounded-md border border-border p-4 bg-muted/20 space-y-3">
        <p className="label-overline">Network ESC/POS printer (optional)</p>
        <p className="text-xs text-muted-foreground">If you have a networked thermal printer, enter its IP and port for direct print from the backend. Otherwise leave blank — the browser print (Ctrl-P) works with any USB/Bluetooth thermal printer via your OS driver.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Receipt printer IP</Label>
            <Input value={state.escpos_printer_ip || ""} onChange={(e) => set("escpos_printer_ip", e.target.value)} placeholder="192.168.1.100" data-testid="printing-receipt-ip" />
          </div>
          <div>
            <Label>Receipt printer port</Label>
            <Input type="number" value={state.escpos_printer_port || 9100} onChange={(e) => set("escpos_printer_port", e.target.value)} data-testid="printing-receipt-port" />
          </div>
          <div>
            <Label>KOT printer IP (kitchen)</Label>
            <Input value={state.escpos_kot_ip || ""} onChange={(e) => set("escpos_kot_ip", e.target.value)} placeholder="Leave blank to reuse receipt printer" data-testid="printing-kot-ip" />
          </div>
          <div>
            <Label>KOT printer port</Label>
            <Input type="number" value={state.escpos_kot_port || 9100} onChange={(e) => set("escpos_kot_port", e.target.value)} data-testid="printing-kot-port" />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} data-testid="printing-save">Save printing settings</Button>
      </div>
    </Card>
  );
}


// ------------------- Approval Workflow Panel -------------------
function ApprovalPanel() {
  const [state, setState] = useState(null);
  const [sim, setSim] = useState({ amount: 15000, category: "utilities", result: null });
  const load = () => api.get("/settings/platform").then((r) => setState(r.data));
  useEffect(() => { load(); }, []);
  if (!state) return null;
  const rules = state.expense_approval_rules || [];
  const setRules = (rr) => setState({ ...state, expense_approval_rules: rr });
  const updateRule = (idx, patch) => {
    const rr = [...rules];
    rr[idx] = { ...rr[idx], ...patch };
    setRules(rr);
  };
  const addRule = () => setRules([...rules, {
    id: `rule-${Date.now()}`,
    name: "New rule",
    min_amount: 0,
    max_amount: null,
    categories: [],
    required_approver_role: "manager",
    active: true,
  }]);
  const removeRule = (idx) => setRules(rules.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const rr = [...rules];
    const j = idx + dir;
    if (j < 0 || j >= rr.length) return;
    [rr[idx], rr[j]] = [rr[j], rr[idx]];
    setRules(rr);
  };
  const save = async () => {
    const { id, updated_at, ...body } = state;
    await api.put("/settings/platform", body);
    toast.success("Approval workflow saved");
    load();
  };
  const runSim = async () => {
    const r = await api.post("/expenses/simulate-approval", { amount: Number(sim.amount), category: sim.category });
    setSim({ ...sim, result: r.data });
  };
  const CATS = ["utilities", "supplies", "maintenance", "salaries", "marketing", "food_bev", "taxes", "rent", "travel", "other"];
  const toggleCat = (idx, cat) => {
    const cur = new Set(rules[idx].categories || []);
    cur.has(cat) ? cur.delete(cat) : cur.add(cat);
    updateRule(idx, { categories: Array.from(cur) });
  };

  return (
    <Card className="p-8 max-w-4xl">
      <div className="flex items-center gap-3 mb-1">
        <CheckCircleIcon size={22} weight="duotone" className="text-primary" />
        <p className="label-overline">Governance</p>
      </div>
      <h3 className="font-display text-2xl">Expense approval workflow</h3>
      <p className="text-sm text-muted-foreground mt-1">
        Design multi-step rules. Rules are evaluated top-down — the first match decides who approves. If none match, expense is auto-approved.
      </p>

      <div className="mt-6 space-y-3">
        {rules.map((rule, idx) => (
          <Card key={rule.id || idx} className="p-4" data-testid={`approval-rule-${idx}`}>
            <div className="flex items-center justify-between mb-2">
              <Input value={rule.name} onChange={(e) => updateRule(idx, { name: e.target.value })} className="max-w-sm font-medium" data-testid={`approval-rule-name-${idx}`} />
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" onClick={() => move(idx, -1)} disabled={idx === 0}>↑</Button>
                <Button size="icon" variant="ghost" onClick={() => move(idx, 1)} disabled={idx === rules.length - 1}>↓</Button>
                <label className="flex items-center gap-1 text-xs cursor-pointer px-2">
                  <input type="checkbox" checked={!!rule.active} onChange={(e) => updateRule(idx, { active: e.target.checked })} data-testid={`approval-rule-active-${idx}`} />
                  <span>Active</span>
                </label>
                <Button size="icon" variant="ghost" onClick={() => removeRule(idx)} className="text-destructive" data-testid={`approval-rule-remove-${idx}`}><TrashIcon size={12} /></Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Min amount (₹)</Label>
                <Input type="number" step="0.01" value={rule.min_amount ?? 0} onChange={(e) => updateRule(idx, { min_amount: Number(e.target.value) })} data-testid={`approval-rule-min-${idx}`} />
              </div>
              <div>
                <Label className="text-xs">Max amount (₹) — blank = ∞</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={rule.max_amount == null ? "" : rule.max_amount}
                  onChange={(e) => updateRule(idx, { max_amount: e.target.value === "" ? null : Number(e.target.value) })}
                  data-testid={`approval-rule-max-${idx}`}
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Required approver</Label>
                <select
                  value={rule.required_approver_role || "manager"}
                  onChange={(e) => updateRule(idx, { required_approver_role: e.target.value })}
                  className="h-10 mt-1 rounded-md border border-input bg-background px-3 text-sm w-full"
                  data-testid={`approval-rule-role-${idx}`}
                >
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-xs">Categories (blank = all)</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {CATS.map((c) => {
                  const on = (rule.categories || []).includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleCat(idx, c)}
                      className={`text-[10px] px-2 py-1 rounded-full border ${on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}
                      data-testid={`approval-rule-cat-${idx}-${c}`}
                    >
                      {c.replace("_", " ")}
                    </button>
                  );
                })}
              </div>
            </div>
          </Card>
        ))}

        {rules.length === 0 && (
          <p className="text-sm text-muted-foreground italic">No rules — every expense is auto-approved.</p>
        )}

        <Button size="sm" variant="outline" onClick={addRule} data-testid="approval-add-rule"><PlusIcon size={12} className="mr-1" /> Add rule</Button>
      </div>

      {/* Simulator */}
      <div className="mt-8 rounded-md border border-border p-4 bg-muted/30">
        <p className="label-overline">Test an expense</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-2 items-end">
          <div>
            <Label className="text-xs">Amount ₹</Label>
            <Input type="number" value={sim.amount} onChange={(e) => setSim({ ...sim, amount: e.target.value })} data-testid="approval-sim-amount" />
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <select
              value={sim.category}
              onChange={(e) => setSim({ ...sim, category: e.target.value })}
              className="h-10 mt-1 rounded-md border border-input bg-background px-3 text-sm w-full"
              data-testid="approval-sim-category"
            >
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <Button onClick={runSim} data-testid="approval-sim-run">Simulate</Button>
          {sim.result && (
            <div className="text-xs" data-testid="approval-sim-result">
              <span className="text-muted-foreground">Outcome:</span> <strong>{sim.result.outcome}</strong>
              {sim.result.rule && <div className="text-[10px] text-muted-foreground">Rule: {sim.result.rule.name}</div>}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end mt-6"><Button onClick={save} data-testid="approval-save-btn">Save workflow</Button></div>
    </Card>
  );
}

function DayEndModeRow({ row, onSaved }) {
  const [time, setTime] = useState(row.auto_day_end_time);
  const [busy, setBusy] = useState(false);
  const save = async (enabled, t) => {
    setBusy(true);
    try {
      const r = await api.post("/platform/day-end-mode", { property_id: row.property_id, enabled, time: t });
      onSaved({ ...row, auto_day_end_enabled: r.data.auto_day_end_enabled, auto_day_end_time: r.data.auto_day_end_time });
      toast.success(enabled ? `${row.name}: Auto Day End at ${r.data.auto_day_end_time} IST` : `${row.name}: Manual Day End`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  const on = row.auto_day_end_enabled;
  const tid = `admin-day-end-${row.property_id}`;
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap py-3 border-t border-border first:border-t-0" data-testid={`${tid}-row`}>
      <p className="text-sm font-medium min-w-[160px]">{row.name}</p>
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`text-xs uppercase tracking-widest ${!on ? "text-primary font-medium" : "text-muted-foreground"}`}>Manual</span>
        <button onClick={() => save(!on, time)} disabled={busy} role="switch" aria-checked={on} data-testid={`${tid}-toggle`}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${on ? "bg-primary" : "bg-muted"}`}>
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? "translate-x-6" : "translate-x-1"}`} />
        </button>
        <span className={`text-xs uppercase tracking-widest ${on ? "text-primary font-medium" : "text-muted-foreground"}`}>Auto</span>
        <input type="time" value={time} onChange={(e) => setTime(e.target.value)} disabled={!on || busy} data-testid={`${tid}-time`}
          className={`h-8 rounded-md border border-input bg-background px-3 text-sm font-mono-plex ${on ? "" : "opacity-50"}`} />
        <Button size="sm" variant="outline" onClick={() => save(on, time)} disabled={!on || busy || time === row.auto_day_end_time} data-testid={`${tid}-time-save`}>Save time</Button>
      </div>
    </div>
  );
}

function DayEndModePanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/platform/day-end-mode").then((r) => setRows(r.data?.properties || [])).catch(() => {}); }, []);
  const onSaved = (u) => setRows((rs) => rs.map((x) => (x.property_id === u.property_id ? u : x)));
  return (
    <div className="mt-6 rounded-md border border-border p-4" data-testid="admin-master-day-end-mode">
      <p className="text-sm font-medium flex items-center gap-2">
        <MoonIcon size={14} weight="duotone" className="text-primary" /> Day End Mode · per property
      </p>
      <p className="text-xs text-muted-foreground mt-1 max-w-lg">
        Manual: front desk closes each day via the Day End dialog. Auto: the scheduler closes yesterday for that property at the chosen time (IST). Only the master owner can change this.
      </p>
      <div className="mt-3">
        {rows.map((r) => <DayEndModeRow key={r.property_id} row={r} onSaved={onSaved} />)}
        {!rows.length && <p className="text-xs text-muted-foreground">No properties yet.</p>}
      </div>
    </div>
  );
}

function MasterPanel({ master, onChanged }) {
  const [users, setUsers] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [confirming, setConfirming] = useState(false);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  const eligible = users.filter((u) => u.email !== master?.master_email);

  const transfer = async () => {
    if (!newEmail) return toast.error("Pick a user");
    setConfirming(true);
    try {
      await api.post("/platform/master", { email: newEmail });
      toast.success(`Master ownership transferred to ${newEmail}. They'll need to log in again to see property CRUD controls.`);
      setNewEmail("");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transfer failed");
    } finally { setConfirming(false); }
  };

  return (
    <Card className="p-8 max-w-3xl">
      <div className="flex items-center gap-3 mb-1">
        <CrownSimpleIcon size={22} weight="duotone" className="text-primary" />
        <p className="label-overline">Ownership</p>
      </div>
      <h3 className="font-display text-2xl">Master owner</h3>
      <p className="text-sm text-muted-foreground mt-1">
        The master owner is the only user who can add, edit or delete a hotel from the group.
        You can transfer this power to another admin at any time.
      </p>

      <Card className="p-4 mt-6 bg-muted/40 border-primary/20">
        <p className="label-overline">Current master</p>
        <p className="font-display text-xl mt-1" data-testid="admin-master-current">{master?.master_email}</p>
      </Card>

      <DayEndModePanel />

      <div className="mt-6 rounded-md border border-border p-4">
        <p className="text-sm font-medium">Transfer master ownership</p>
        <p className="text-xs text-muted-foreground mt-1">Pick another admin. If they aren't an admin yet, we'll upgrade them automatically.</p>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <select
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="admin-master-new-select"
          >
            <option value="">Choose a user…</option>
            {eligible.map((u) => (
              <option key={u.id} value={u.email}>{u.name} · {u.email} ({u.role})</option>
            ))}
          </select>
          <Button
            variant="destructive"
            disabled={!newEmail || confirming}
            onClick={() => {
              if (window.confirm(`Transfer master ownership to ${newEmail}?\n\nYou will lose the ability to add or delete properties immediately. This can only be reversed by the new master.`)) transfer();
            }}
            data-testid="admin-master-transfer-btn"
          >
            <CrownSimpleIcon size={14} className="mr-1" /> Transfer ownership
          </Button>
        </div>
      </div>

      <div className="mt-6 rounded-md border-2 border-destructive/40 bg-destructive/5 p-4" data-testid="admin-master-factory-reset">
        <p className="text-sm font-medium text-destructive">Danger zone · Handover / Factory reset</p>
        <p className="text-xs text-muted-foreground mt-1">
          Wipe every reservation, invoice, POS order, guest, expense, task and audit log — leaving only the master owner + properties &amp; rooms setup.
          Use this ONCE before handing the platform to a new owner. A pre-reset backup is auto-created for safety.
        </p>
        <Button
          variant="destructive"
          className="mt-3"
          onClick={async () => {
            const phrase = window.prompt('This will PERMANENTLY delete all business data. Type "DELETE ALL DATA" to confirm:');
            if (phrase !== "DELETE ALL DATA") { toast.info("Reset cancelled."); return; }
            const keepProps = window.confirm("Keep the property + room setup?\n\nOK = keep properties & rooms (recommended for handover).\nCancel = wipe everything including properties.");
            try {
              const r = await api.post("/admin/factory-reset", { confirm: "DELETE ALL DATA", keep_properties: keepProps, keep_rooms: keepProps }, { silent: true });
              const deleted = r.data?.deleted || {};
              const summary = Object.entries(deleted).filter(([, v]) => v > 0).map(([k, v]) => `${k}: ${v}`).join(", ");
              toast.success(`Factory reset complete. ${summary || "System is fresh."}`);
              setTimeout(() => window.location.reload(), 1500);
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Reset failed");
            }
          }}
          data-testid="admin-master-factory-reset-btn"
        >
          Reset all data (handover)
        </Button>
      </div>
    </Card>
  );
}


// ------------------- Meal Plans Panel -------------------
function MealPlansPanel() {
  const [state, setState] = useState(null);
  useEffect(() => { api.get("/settings/platform").then((r) => setState(r.data)); }, []);
  if (!state) return null;
  const plans = state.meal_plans || [];
  const set = (k, v) => setState({ ...state, [k]: v });
  const updatePlan = (idx, patch) => {
    const next = [...plans];
    next[idx] = { ...next[idx], ...patch };
    set("meal_plans", next);
  };
  const save = async () => {
    const { id, updated_at, ...body } = state;
    await api.put("/settings/platform", body);
    toast.success("Meal plans saved");
  };
  return (
    <Card className="p-6 max-w-3xl" data-testid="admin-meal-plans-panel">
      <p className="label-overline">Hospitality</p>
      <h3 className="font-display text-2xl">Meal Plans (EP · CP · MAP · AP)</h3>
      <p className="text-sm text-muted-foreground mt-1">Per-person per-night surcharge applied on top of room rate when a booking picks the plan.</p>
      <div className="mt-5 space-y-3">
        {plans.map((p, idx) => (
          <div key={p.code} className="grid grid-cols-12 gap-2 items-center border-b border-border py-2" data-testid={`mealplan-row-${p.code}`}>
            <div className="col-span-2 font-mono-plex font-semibold">{p.code}</div>
            <div className="col-span-6"><Input value={p.label} onChange={(e) => updatePlan(idx, { label: e.target.value })} /></div>
            <div className="col-span-3"><Input type="number" min="0" value={p.surcharge_per_person} onChange={(e) => updatePlan(idx, { surcharge_per_person: Number(e.target.value) })} data-testid={`mealplan-rate-${p.code}`} /></div>
            <div className="col-span-1 text-xs text-muted-foreground">/pax/nt</div>
          </div>
        ))}
      </div>
      <div className="mt-6 grid grid-cols-2 gap-4">
        <div>
          <Label>Extra bed rate (₹/night)</Label>
          <Input type="number" value={state.extra_bed_rate || 0} onChange={(e) => set("extra_bed_rate", Number(e.target.value))} data-testid="admin-extra-bed-rate" />
        </div>
        <div>
          <Label>CNB (Child No Bed) rate (₹/night)</Label>
          <Input type="number" value={state.cnb_rate || 0} onChange={(e) => set("cnb_rate", Number(e.target.value))} data-testid="admin-cnb-rate" />
        </div>
      </div>
      <div className="flex justify-end mt-6"><Button onClick={save} data-testid="admin-meal-plans-save">Save meal plans</Button></div>
    </Card>
  );
}

// ------------------- Sidebar Menu Order Panel -------------------
const DEFAULT_ORDER = [
  "dashboard","front_desk","reservations","rate_calendar","rooms","guests","housekeeping",
  "invoices","channels","properties","staff","attendance","payroll","expenses","pos",
  "halls","amenities","reports","audit","help","admin",
];
const LABELS = {
  dashboard: "Dashboard", front_desk: "Front Desk (Timeline)", reservations: "Reservations",
  rate_calendar: "Rate Calendar", rooms: "Rooms", guests: "Guests", housekeeping: "Housekeeping",
  invoices: "Invoices", channels: "Channels", properties: "Properties", staff: "Staff",
  attendance: "Attendance", payroll: "Payroll", expenses: "Expenses", pos: "Restaurant POS",
  halls: "Halls & Events", amenities: "Amenities", reports: "Reports", audit: "Audit",
  help: "Help", admin: "Admin Console",
};

function MenuOrderPanel() {
  const [state, setState] = useState(null);
  const [scope, setScope] = useState("global");   // "global" | role id
  useEffect(() => { api.get("/settings/platform").then((r) => setState(r.data)); }, []);
  if (!state) return null;
  const perRole = state.menu_order_by_role || {};
  const isRoleScope = scope !== "global";
  const currentOrder = (isRoleScope ? perRole[scope] : state.menu_order) || [];
  const order = (currentOrder.length ? currentOrder : DEFAULT_ORDER).slice();

  const setOrder = (next) => {
    if (isRoleScope) {
      setState({ ...state, menu_order_by_role: { ...perRole, [scope]: next } });
    } else {
      setState({ ...state, menu_order: next });
    }
  };
  const move = (idx, delta) => {
    const j = idx + delta;
    if (j < 0 || j >= order.length) return;
    const next = order.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    setOrder(next);
  };
  const reset = () => {
    if (isRoleScope) {
      const next = { ...perRole }; delete next[scope];
      setState({ ...state, menu_order_by_role: next });
    } else {
      setState({ ...state, menu_order: [] });
    }
  };
  const save = async () => {
    const { id, updated_at, ...body } = state;
    await api.put("/settings/platform", body);
    toast.success(`Sidebar order saved (${isRoleScope ? scope.replace("_", " ") : "global"}) — refresh to see it applied.`);
  };

  const roles = [
    { id: "global", label: "Global (fallback for all)" },
    { id: "admin", label: "Admin" },
    { id: "manager", label: "Manager" },
    { id: "front_desk", label: "Front Desk" },
    { id: "housekeeping", label: "Housekeeping" },
    { id: "restaurant_manager", label: "Restaurant Manager" },
    { id: "service", label: "Service / Waiter" },
  ];

  return (
    <Card className="p-6 max-w-2xl" data-testid="admin-menu-order-panel">
      <p className="label-overline">Layout</p>
      <h3 className="font-display text-2xl">Sidebar menu order</h3>
      <p className="text-sm text-muted-foreground mt-1">
        Pick a scope, then use ↑ ↓ to reorder. <b>Per-role orders override the global order</b> for that role.
        If a role has no custom order it falls back to global.
      </p>

      <div className="mt-4">
        <Label className="text-xs">Scope</Label>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger data-testid="admin-menu-order-scope"><SelectValue /></SelectTrigger>
          <SelectContent>
            {roles.map((r) => (
              <SelectItem key={r.id} value={r.id}>
                {r.label}{r.id !== "global" && perRole[r.id]?.length ? " · custom" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isRoleScope && !perRole[scope]?.length && (
          <p className="text-[11px] text-muted-foreground mt-2">This role currently inherits the global order. Reorder below to create a custom one.</p>
        )}
      </div>

      <div className="mt-4 border border-border rounded-md overflow-hidden">
        {order.map((id, idx) => (
          <div key={id} className="flex items-center justify-between border-b border-border last:border-b-0 px-3 py-2 hover:bg-muted/40" data-testid={`menu-order-row-${id}`}>
            <div className="flex items-center gap-3">
              <span className="text-[10px] font-mono-plex text-muted-foreground w-6">{idx + 1}</span>
              <span className="text-sm">{LABELS[id] || id}</span>
            </div>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`menu-order-up-${id}`}>↑</Button>
              <Button size="icon" variant="ghost" onClick={() => move(idx, 1)} disabled={idx === order.length - 1} data-testid={`menu-order-down-${id}`}>↓</Button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-6">
        <Button variant="ghost" onClick={reset} data-testid="admin-menu-order-reset">
          {isRoleScope ? "Remove custom order (inherit global)" : "Reset to default"}
        </Button>
        <Button onClick={save} data-testid="admin-menu-order-save">Save order</Button>
      </div>
    </Card>
  );
}
